import {
  spot as spotCore,
  type AutopilotControlTone,
  type AutopilotRefusal,
  type AutopilotRefusalCode,
  type AutopilotView,
  type GateSkipReason,
  type SpotView,
} from "@auto-pools/core";
import { formatAgo, formatMoney, formatWhen } from "../lib/format";
import { refusalText } from "./autopilotRefusalText";
import { oldestExitNowAt, unvaluedFunded } from "./autopilotCycles";
import { spotCardStatus } from "./spot/spotPresentation";

/**
 * The runner revalues a held position on a ~60 s cadence (`VALUE_EVERY_MS`,
 * apps/server/src/autopilot/cycle.ts). Twice that with no newer valuation means the loop stalled or
 * every attempt since has failed — the pools result still carries the last valuation, so it must
 * stop reading as a live result rather than silently show a result that stopped moving.
 */
export const STALE_VALUATION_MS = 2 * 60_000;
/** The spot card's own bound for its prices (`PRICES_STALE_MS` in spot/SpotCardExpanded.tsx). */
export const SPOT_PRICES_STALE_MS = 2 * 60_000;

/** Today's wording (design 47 strip, `shapeText`) with its tone; `line` is null in normal states. */
interface EngineShape {
  tone: AutopilotControlTone;
  line: string | null;
}

function sentence(text: string, detail: string | null) {
  return detail ? `${text} — ${detail}` : text;
}

/**
 * The market gate's refusals (core `GateSkipReason`): a normal wait for a calm price, said by the In
 * pools cell alone. Every other refusal is a data or funding problem the operator may act on (e.g.
 * not enough SOL for fees), so it keeps its own sentence on the second line.
 */
const MARKET_GATE_WAITS = {
  insufficient_history: true,
  reentry_blocked: true,
  high_volatility: true,
  far_from_mean: true,
  strong_trend: true,
} as const satisfies Record<GateSkipReason, true>;

function isMarketGateWait(code: AutopilotRefusalCode): boolean {
  return Object.hasOwn(MARKET_GATE_WAITS, code);
}

function refusalDetail(refusal: AutopilotRefusal): string {
  return (
    refusalText(refusal) + (refusal.retryAt ? ` · ranks again ${formatWhen(refusal.retryAt)}` : "")
  );
}

/**
 * The pools engine: a running run keeps the strip's own tones (an idle wait is grey, not green);
 * the other states take core `poolsTone`, so a hold, a limit or stopping is amber and money the run
 * cannot manage is red. The line is the strip's sentence for anything that is not a normal state.
 */
export function poolsShape(view: AutopilotView): EngineShape {
  const { status, candidate } = view;
  const coreTone = spotCore.poolsTone(status);
  if (status.state === "off") return { tone: "neutral", line: null };
  if (status.state === "paused") return { tone: coreTone, line: "Autopilot paused" };
  if (status.hold === "vault-locked") return { tone: coreTone, line: "Autopilot · vault locked" };
  if (status.hold === "recovering") return { tone: coreTone, line: "Autopilot · recovering" };
  if (status.state === "needs-attention")
    return { tone: coreTone, line: "Autopilot needs attention" };
  if (status.state === "limit" && status.reason?.state === "limit") {
    const limit = status.reason.limit;
    return {
      tone: coreTone,
      line:
        limit === "entries"
          ? sentence(
              "Stopped · entry limit reached",
              status.reason.freesAt ? `frees ${formatWhen(status.reason.freesAt)}` : null,
            )
          : "Stopped · drawdown limit reached",
    };
  }
  const cycles = status.cycles;
  if (cycles.length > 0) {
    const inFlight = cycles.find((cycle) => cycle.phase !== "open");
    if (inFlight) {
      const text =
        cycles.length > 1
          ? `In ${String(cycles.length)} positions`
          : inFlight.phase === "opening"
            ? `Opening · ${inFlight.pair}`
            : `Closing · ${inFlight.pair}`;
      return { tone: "warn", line: sentence(text, "an operation is in flight") };
    }
    // In position / In N positions: the figures beside the dot already say it.
    return {
      tone: status.state === "stopping" ? coreTone : "good",
      line: status.state === "stopping" ? "Stopping…" : null,
    };
  }
  if (status.state === "stopping") return { tone: coreTone, line: "Stopping…" };
  // Design 48, section 3: a whole-mode balance below the entry minimum is an amber wait, not an
  // error and not a stop — it takes priority over the generic candidate refusal below.
  if (status.capitalShort)
    return {
      tone: "warn",
      line: sentence(
        "Waiting — the free balance is below the minimum",
        `${formatMoney(status.capitalShort.available)} free, the smallest entry is ${formatMoney(status.capitalShort.minimum)}. Nothing is stopped: it opens as soon as you top up.`,
      ),
    };
  const refusal = candidate?.refusal;
  if (refusal?.code === "no_candidate")
    return {
      tone: "warn",
      line: sentence("No pool passed the filters", refusalDetail(refusal)),
    };
  if (refusal && !isMarketGateWait(refusal.code))
    return { tone: "neutral", line: sentence("Not entering", refusalDetail(refusal)) };
  // Waiting for a calm price / for the next scan: a normal wait, said by the In pools cell.
  return { tone: "neutral", line: null };
}

/** Why In pools is "—" while it holds nothing (design 58: a reason, never a zero). */
export function poolsEmptyReason(view: AutopilotView | null): string | null {
  if (!view) return null;
  const { status, candidate } = view;
  if (status.state === "off") return "autopilot off";
  if (status.state === "paused") return "paused";
  const refusal = candidate?.refusal;
  if (!refusal) return "waiting for the next scan";
  if (refusal.code === "no_candidate") return "no pool passed the filters";
  return isMarketGateWait(refusal.code) ? "waiting for a calm price" : "waiting to enter";
}

/** The spot engine: core `spotTone`; the card's own chip text only when it warns. */
export function spotShape(spot: SpotView): EngineShape {
  const status = spotCardStatus(spot);
  return {
    tone: status.tone,
    line: status.tone === "warn" || status.tone === "bad" ? `Spot · ${status.label}` : null,
  };
}

/** The In spot cell's label and, while it holds no coin, the reason beside its "—". */
export function spotCell(spot: SpotView): { label: string; emptyReason: string | null } {
  const coins = spot.coins.length;
  if (coins > 0)
    return {
      label: `In spot · ${String(coins)} ${coins === 1 ? "coin" : "coins"}`,
      emptyReason: null,
    };
  switch (spot.lifecycle) {
    case "running":
      return { label: "In spot · all in USDT", emptyReason: "waiting in USDT" };
    case "starting":
      return { label: "In spot", emptyReason: "starting" };
    case "stopping":
      return { label: "In spot", emptyReason: "stopping" };
    case "paused":
      return { label: "In spot", emptyReason: "paused" };
    case "off":
      return {
        label: spot.key.usable ? "In spot" : "In spot · no key",
        emptyReason: "not running",
      };
  }
}

function age(at: string | null | undefined, now: number): number | null {
  if (!at) return null;
  const time = Date.parse(at);
  return Number.isFinite(time) ? now - time : null;
}

export interface Staleness {
  /** The In pools marker: today's strip wording for its All time figure. */
  pools: string | null;
  /** The In spot marker: the spot snapshot's prices are older than the card's bound. */
  spot: string | null;
  /** Total rests on both parts, so it is stale when either is. */
  total: boolean;
}

/** Every valuation time the stale markers age; the row ticks only while one exists. */
export function valuationTimes(view: AutopilotView | null): (string | null)[] {
  if (!view) return [];
  return [
    oldestExitNowAt(view.status.cycles),
    view.results ? view.results.pools.valuedAt : view.status.allTimeValuedAt,
    view.results?.spot?.valuedAt ?? null,
  ];
}

export function staleness(view: AutopilotView | null, now: number): Staleness {
  if (!view) return { pools: null, spot: null, total: false };
  const { status, results } = view;
  const exitNowAt = oldestExitNowAt(status.cycles);
  const exitNowAge = age(exitNowAt, now);
  const staleReturn = exitNowAge !== null && exitNowAge >= STALE_VALUATION_MS;
  // `results.pools` is filled while the autopilot is off too; older views carry only the status.
  const poolsValuedAt = results ? results.pools.valuedAt : status.allTimeValuedAt;
  const poolsFlag = results ? results.pools.stale : status.allTimeStale;
  const poolsAge = age(poolsValuedAt, now);
  const staleAllTime = poolsFlag || (poolsAge !== null && poolsAge >= STALE_VALUATION_MS);
  const pools = unvaluedFunded(status.cycles)
    ? "stale · not valued"
    : staleReturn
      ? `stale · valued ${formatAgo(exitNowAt, now)}`
      : staleAllTime
        ? "stale"
        : null;
  const spotValuedAt = results?.spot?.valuedAt ?? null;
  const spotAge = age(spotValuedAt, now);
  const spot =
    spotAge !== null && spotAge > SPOT_PRICES_STALE_MS
      ? `stale · prices ${formatAgo(spotValuedAt, now)}`
      : null;
  return { pools, spot, total: pools !== null || spot !== null };
}

export interface SummaryRowState {
  tone: AutopilotControlTone;
  /** The second line's engine sentences; empty in normal states, so no second line is drawn. */
  lines: string[];
}

/**
 * The dot and panel tone: the worse of the two engines; the second line only for engine news.
 * Staleness never adds a line: the in-cell markers say it, and a line that appears for the moment a
 * returning tab re-reads its valuation would make the whole list jump.
 */
export function summaryRowState(view: AutopilotView | null): SummaryRowState {
  if (!view) return { tone: "neutral", lines: [] };
  const pools = poolsShape(view);
  const spot = view.spot ? spotShape(view.spot) : null;
  const tone = spot ? spotCore.worseTone(pools.tone, spot.tone) : pools.tone;
  const lines = [pools.line, spot?.line ?? null].filter((line): line is string => line !== null);
  return { tone, lines };
}
